'use client';

import { motion } from 'framer-motion';

const BAR_COUNT = 5;

/**
 * Two designed sizes, not one scaled one.
 *
 * `instrument` is the recording UI's meter: an object the member watches while
 * they speak, sized to read across a screen. Unchanged, and the default, so
 * every existing call site keeps exactly the meter it had.
 *
 * `caption` is the welcome mic check's: a mark that lives inside a line of
 * 13px body text. Its geometry is DERIVED from that text rather than scaled
 * from `instrument`, because the instrument's 1:8 bar proportion does not
 * survive the shrink — a uniform 9/32 scale would put the bars at 1.1px wide,
 * below the width at which a rounded pill renders as anything but a smudge.
 * What carries across a size change is the rule, not the ratio:
 *
 *   maxHeight = the caption's CAP HEIGHT. DM Sans caps are 700/1000 em, so
 *               13px x 0.70 = 9.1 -> 9 (verified in Chromium:
 *               `measureText('H').actualBoundingBoxAscent` is 9.1 at 13px and
 *               10.5 at 15px). A bar at full tilt is therefore exactly as tall
 *               as the capital S in the sentence beside it, and — with the row
 *               baseline-aligned — the whole meter fits inside one line of
 *               text instead of standing next to it as a 32px instrument.
 *   minHeight = barWidth, so a resting bar is a round dot sitting on the
 *               baseline rather than a clipped stub.
 *   barWidth/gap = 3/2, the smallest pair at which five marks stay separately
 *               legible at 1x device pixel ratio.
 */
export const WAVEFORM_GEOMETRY = {
  instrument: { maxHeight: 32, minHeight: 4, barWidth: 4, gap: 3 },
  caption: { maxHeight: 9, minHeight: 3, barWidth: 3, gap: 2 },
} as const;

export type WaveformScale = keyof typeof WAVEFORM_GEOMETRY;

/**
 * The rendered width of the bar cluster.
 *
 * Exported because a caller that reserves the meter's slot while the meter
 * itself is absent has to hold exactly this width, and a hand-copied number
 * drifts the moment the geometry above changes.
 */
export function waveformWidth(scale: WaveformScale, shape: WaveformShape = 'bands'): number {
  const { barWidth, gap } = WAVEFORM_GEOMETRY[scale];
  const count = shape === 'bell' ? BELL_BANDS.length : BAR_COUNT;
  return count * barWidth + (count - 1) * gap;
}

/**
 * Bar colour. `seafoam` is the original quiet mark (about 1.4:1 on cream, so it
 * can only ever decorate). `accent` is Harbor Teal, for a meter that has to read
 * as live status on White Paper: about 4.1:1, past the 3:1 non-text bar
 * (Terracotta would sit at 2.9:1). The answer dock's listening state uses it.
 */
export type WaveformTone = 'seafoam' | 'accent';

const WAVEFORM_TONE_CLASS: Record<WaveformTone, string> = {
  seafoam: 'bg-seafoam',
  accent: 'bg-accent',
};

/**
 * How the five frequency bands are laid out.
 *
 * `bands`: one bar per band, low to high, left to right. Speech lives in the
 * low bands, so this leans hard to the left; it stays for the small marks
 * (welcome mic check, inline probe) that sit beside text.
 *
 * `bell`: seven bars, centre-out. The loudest band (the lowest) stands in the
 * middle, the next two either side of it, the quietest at the edges, and an
 * envelope tapers the sides, so the meter breathes evenly about its centre
 * (owner feedback 2026-10-05: the answer dock's meter was "heavily
 * left-skewed"). The two sides use different bands, so it is balanced without
 * being a mirror image.
 */
export type WaveformShape = 'bands' | 'bell';

/** Band index per bar, left to right, for the bell. */
export const BELL_BANDS = [4, 3, 1, 0, 2, 3, 4] as const;
/** Height envelope per bar for the bell: full in the middle, tapering out. */
export const BELL_ENVELOPE = [0.5, 0.72, 0.9, 1, 0.9, 0.72, 0.5] as const;

/** Bar heights (px) for a frame of band levels, in left-to-right order. */
export function waveformBarHeights(
  levels: readonly number[],
  isActive: boolean,
  { scale = 'instrument', shape = 'bands' }: { scale?: WaveformScale; shape?: WaveformShape } = {},
): number[] {
  const { maxHeight, minHeight } = WAVEFORM_GEOMETRY[scale];
  const span = maxHeight - minHeight;
  if (shape === 'bell') {
    return BELL_BANDS.map((band, i) =>
      isActive ? minHeight + Math.min(1, levels[band] ?? 0) * BELL_ENVELOPE[i] * span : minHeight,
    );
  }
  return Array.from({ length: BAR_COUNT }, (_, i) =>
    isActive ? minHeight + (levels[i] ?? 0) * span : minHeight,
  );
}

interface WaveformProps {
  levels: number[];
  isActive: boolean;
  /** Defaults to the recording UI's size. */
  scale?: WaveformScale;
  /** @default 'seafoam' */
  tone?: WaveformTone;
  /** @default 'bands' */
  shape?: WaveformShape;
}

export function Waveform({
  levels,
  isActive,
  scale = 'instrument',
  tone = 'seafoam',
  shape = 'bands',
}: WaveformProps) {
  const { maxHeight, barWidth, gap } = WAVEFORM_GEOMETRY[scale];
  const heights = waveformBarHeights(levels, isActive, { scale, shape });
  // The bell is the answer dock's live status: near-critically damped, so it
  // follows the voice without the overshoot the small marks keep.
  const heightSpring =
    shape === 'bell'
      ? { type: 'spring' as const, stiffness: 300, damping: 32 }
      : { type: 'spring' as const, stiffness: 300, damping: 20 };

  return (
    // The box is exactly `maxHeight` tall with the bars anchored to its bottom,
    // so its bottom border edge IS the bars' feet. That is what lets a
    // baseline-aligned parent row put those feet on a line of text's baseline
    // without any offset: a flex item with no in-flow line boxes synthesises
    // its baseline from its bottom margin edge.
    <div
      className="flex items-end justify-center"
      style={{ gap, height: maxHeight }}
      role="img"
      aria-label={isActive ? 'Audio waveform active' : 'Audio waveform inactive'}
    >
      {heights.map((height, i) => {
        return (
          <motion.div
            key={i}
            className={`rounded-full ${WAVEFORM_TONE_CLASS[tone]}`}
            style={{ width: barWidth }}
            animate={{
              height,
              opacity: isActive ? 1 : 0.3,
            }}
            transition={{
              height: heightSpring,
              opacity: { duration: 0.2 },
            }}
          />
        );
      })}
    </div>
  );
}
